import React, {useState, useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {useAuth} from '../contexts/AuthContext';
import {usePhase} from '../contexts/PhaseContext';
import {apiUrl} from '../contexts/api-url';

export const Dashboard = () => (
    <div className="p-6">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p>Welcome to your dashboard!</p>
    </div>
);

export const Upload = () => {
    const {user} = useAuth();
    const {phase, isPhaseAllowed} = usePhase();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        medium: '',
        dimensions: '',
        yearCreated: '',
    });

    const [selectedImages, setSelectedImages] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [errors, setErrors] = useState({});

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData((prev) => ({...prev, [name]: value}));

        // Clear field-specific errors
        if (errors[name]) {
            setErrors((prev) => ({...prev, [name]: ''}));
        }
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);

        if (files.length === 0) return;

        if (files.length > 5) {
            toast.error('Maximum 5 images allowed');
            return;
        }

        // Validate file types
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        const invalidFiles = files.filter((file) => !validTypes.includes(file.type));

        if (invalidFiles.length > 0) {
            toast.error('Only JPEG, PNG, GIF, and WebP images are allowed');
            return;
        }

        // Validate file sizes (10MB each)
        const maxSize = 10 * 1024 * 1024;
        const oversizedFiles = files.filter((file) => file.size > maxSize);

        if (oversizedFiles.length > 0) {
            toast.error('Each image must be less than 10MB');
            return;
        }

        setSelectedImages(files);

        // Create preview URLs
        const newPreviews = files.map((file) => URL.createObjectURL(file));
        setPreviews(newPreviews);
    };

    const removeImage = (index) => {
        const newImages = selectedImages.filter((_, i) => i !== index);
        const newPreviews = previews.filter((_, i) => i !== index);

        // Revoke the URL to prevent memory leaks
        URL.revokeObjectURL(previews[index]);

        setSelectedImages(newImages);
        setPreviews(newPreviews);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        } else if (formData.title.length > 200) {
            newErrors.title = 'Title must be less than 200 characters';
        }

        if (formData.description.length > 2000) {
            newErrors.description = 'Description must be less than 2000 characters';
        }

        if (formData.medium.length > 100) {
            newErrors.medium = 'Medium must be less than 100 characters';
        }

        if (formData.dimensions.length > 100) {
            newErrors.dimensions = 'Dimensions must be less than 100 characters';
        }

        if (formData.yearCreated && (isNaN(formData.yearCreated) || formData.yearCreated < 1800 || formData.yearCreated > new Date().getFullYear())) {
            newErrors.yearCreated = `Year must be between 1800 and ${new Date().getFullYear()}`;
        }

        if (selectedImages.length === 0) {
            newErrors.images = 'At least one image is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setUploading(true);

        try {
            const formDataToSend = new FormData();

            // Add form fields
            formDataToSend.append('title', formData.title);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('medium', formData.medium);
            formDataToSend.append('dimensions', formData.dimensions);
            if (formData.yearCreated) {
                formDataToSend.append('yearCreated', formData.yearCreated);
            }

            // Add images
            selectedImages.forEach((image, index) => {
                formDataToSend.append('images', image);
            });

            const response = await axios.post('/api/art/upload', formDataToSend, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            toast.success('Artwork uploaded successfully!');
            navigate('/my-art');
        } catch (error) {
            console.error('Upload error:', error);
            const errorMessage = error.response?.data?.error || 'Upload failed';
            toast.error(errorMessage);

            if (error.response?.data?.details) {
                const validationErrors = {};
                error.response.data.details.forEach((detail) => {
                    validationErrors[detail.param] = detail.msg;
                });
                setErrors(validationErrors);
            }
        } finally {
            setUploading(false);
        }
    };

    // Cleanup preview URLs on unmount
    useEffect(() => {
        return () => {
            previews.forEach((preview) => URL.revokeObjectURL(preview));
        };
    }, [previews]);

    if (!isPhaseAllowed('upload')) {
        return (
            <div className="p-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fillRule="evenodd"
                                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">Upload Not Available</h3>
                            <div className="mt-2 text-sm text-yellow-700">
                                <p>
                                    Art uploads are only allowed during the "Uploading" phase. Current phase:{' '}
                                    <span className="font-semibold">{phase.currentPhase}</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h1 className="text-2xl font-bold text-gray-900">Upload Artwork</h1>
                    <p className="mt-1 text-sm text-gray-600">Share your artwork with the community. You can upload up to 5 images per piece.</p>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Image Upload */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Images <span className="text-red-500">*</span>
                        </label>

                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                            <div className="space-y-1 text-center">
                                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                    <path
                                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <div className="flex text-sm text-gray-600">
                                    <label
                                        htmlFor="images"
                                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                                    >
                                        <span>Upload images</span>
                                        <input
                                            id="images"
                                            name="images"
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            className="sr-only"
                                        />
                                    </label>
                                    <p className="pl-1">or drag and drop</p>
                                </div>
                                <p className="text-xs text-gray-500">PNG, JPG, GIF, WebP up to 10MB each (max 5 images)</p>
                            </div>
                        </div>

                        {errors.images && <p className="mt-1 text-sm text-red-600">{errors.images}</p>}

                        {/* Image Previews */}
                        {previews.length > 0 && (
                            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                                {previews.map((preview, index) => (
                                    <div key={index} className="relative">
                                        <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-32 object-cover rounded-lg shadow-md" />
                                        <button
                                            type="button"
                                            onClick={() => removeImage(index)}
                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Title */}
                    <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                            Title <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter artwork title"
                        />
                        {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                    </div>

                    {/* Description */}
                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                            Description
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            rows={4}
                            value={formData.description}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Describe your artwork, techniques used, inspiration, etc."
                        />
                        {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
                    </div>

                    {/* Medium and Dimensions */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="medium" className="block text-sm font-medium text-gray-700">
                                Medium
                            </label>
                            <input
                                type="text"
                                id="medium"
                                name="medium"
                                value={formData.medium}
                                onChange={handleInputChange}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g., Oil on canvas, Digital art, Watercolor"
                            />
                            {errors.medium && <p className="mt-1 text-sm text-red-600">{errors.medium}</p>}
                        </div>

                        <div>
                            <label htmlFor="dimensions" className="block text-sm font-medium text-gray-700">
                                Dimensions
                            </label>
                            <input
                                type="text"
                                id="dimensions"
                                name="dimensions"
                                value={formData.dimensions}
                                onChange={handleInputChange}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g., 24x36 inches, 30x40 cm"
                            />
                            {errors.dimensions && <p className="mt-1 text-sm text-red-600">{errors.dimensions}</p>}
                        </div>
                    </div>

                    {/* Year Created */}
                    <div>
                        <label htmlFor="yearCreated" className="block text-sm font-medium text-gray-700">
                            Year Created
                        </label>
                        <input
                            type="number"
                            id="yearCreated"
                            name="yearCreated"
                            value={formData.yearCreated}
                            onChange={handleInputChange}
                            min="1800"
                            max={new Date().getFullYear()}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder={new Date().getFullYear().toString()}
                        />
                        {errors.yearCreated && <p className="mt-1 text-sm text-red-600">{errors.yearCreated}</p>}
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end space-x-4">
                        <button
                            type="button"
                            onClick={() => navigate('/dashboard')}
                            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={uploading}
                            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {uploading ? 'Uploading...' : 'Upload Artwork'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export const Voting = () => {
    const {user} = useAuth();
    const {phase, isPhaseAllowed} = usePhase();
    const navigate = useNavigate();
    const [currentArt, setCurrentArt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submittingVote, setSubmittingVote] = useState(false);
    const [showMatch, setShowMatch] = useState(false);
    const [matchData, setMatchData] = useState(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [eligibilityError, setEligibilityError] = useState(null);
    const [stats, setStats] = useState({
        votesGiven: {total: 0, likes: 0, passes: 0},
        matches: 0,
    });

    const checkVotingEligibility = async () => {
        try {
            const response = await axios.get('/api/art/voting-eligibility');
            if (!response.data.canVote) {
                setEligibilityError(response.data);
                return false;
            }
            setEligibilityError(null);
            return true;
        } catch (error) {
            console.error('Error checking voting eligibility:', error);
            setEligibilityError({
                reason: 'error',
                message: 'Failed to check voting eligibility',
            });
            return false;
        }
    };

    const fetchRandomArt = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/art/random');

            if (response.data.art) {
                setCurrentArt(response.data.art);
                setSelectedImageIndex(0); // Reset image index for new art
            } else {
                setCurrentArt(null);
            }
        } catch (error) {
            console.error('Error fetching random art:', error);

            // Check if it's an eligibility error
            if (error.response?.status === 403 && error.response?.data?.canVote === false) {
                setEligibilityError(error.response.data);
                setCurrentArt(null);
            } else {
                toast.error('Failed to load artwork');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchVotingStats = async () => {
        try {
            const response = await axios.get('/api/votes/stats');
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching voting stats:', error);
        }
    };

    const submitVote = async (voteValue) => {
        if (!currentArt || submittingVote) return;

        try {
            setSubmittingVote(true);
            const response = await axios.post('/api/votes/', {
                artId: currentArt.id,
                vote: voteValue,
            });

            if (response.data.isMatch) {
                setMatchData(response.data.match);
                setShowMatch(true);

                // Show match notification
                toast.success("🎉 It's a match! Both artists will be notified.", {
                    duration: 5000,
                    position: 'bottom-center',
                });
            } else {
                toast.success(voteValue ? '💚 Liked!' : '👋 Passed');
            }

            // Fetch next art piece
            await fetchRandomArt();
            await fetchVotingStats();
        } catch (error) {
            console.error('Error submitting vote:', error);
            const errorMessage = error.response?.data?.error || 'Failed to submit vote';
            toast.error(errorMessage);
        } finally {
            setSubmittingVote(false);
        }
    };

    const handleLike = () => submitVote(true);
    const handlePass = () => submitVote(false);

    const closeMatchModal = () => {
        setShowMatch(false);
        setMatchData(null);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const getArtistInitials = (artist) => {
        if (!artist) return '?';
        return `${artist.firstName?.charAt(0) || ''}${artist.lastName?.charAt(0) || ''}`;
    };

    useEffect(() => {
        if (isPhaseAllowed('vote')) {
            checkVotingEligibility().then((canVote) => {
                if (canVote) {
                    fetchRandomArt();
                    fetchVotingStats();
                }
            });
        }
    }, [isPhaseAllowed]);

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (submittingVote || !currentArt || eligibilityError) return;

            if (e.key === 'ArrowLeft' || e.key === 'x' || e.key === 'X') {
                e.preventDefault();
                handlePass();
            } else if (e.key === 'ArrowRight' || e.key === 'l' || e.key === 'L') {
                e.preventDefault();
                handleLike();
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [submittingVote, currentArt, eligibilityError]);

    if (!isPhaseAllowed('vote')) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fillRule="evenodd"
                                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">Voting Not Available</h3>
                            <div className="mt-2 text-sm text-yellow-700">
                                <p>
                                    Voting is only allowed during the "Voting" phase. Current phase:{' '}
                                    <span className="font-semibold">{phase.currentPhase}</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show eligibility error if user can't vote
    if (eligibilityError) {
        const isNoArt = eligibilityError.reason === 'no_art_uploaded';
        const isAllMatched = eligibilityError.reason === 'all_art_matched';
        const isVotingPhase = phase.currentPhase === 'Voting';

        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="text-center py-12">
                    <div
                        className={`rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 ${
                            isNoArt ? 'bg-orange-100' : 'bg-green-100'
                        }`}
                    >
                        {isNoArt ? (
                            <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        ) : (
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        )}
                    </div>

                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {isNoArt ? (isVotingPhase ? 'Sitting this one out' : 'Upload artwork to start voting') : 'Congratulations!'}
                    </h3>

                    <p className="text-gray-600 mb-6">
                        {isNoArt
                            ? isVotingPhase
                                ? "You didn't upload any art, so you're sitting this one out. Wait until the cycle restarts to add some art."
                                : eligibilityError.message
                            : isVotingPhase
                            ? 'All your art has been matched! You can upload more when the cycle restarts.'
                            : eligibilityError.message}
                    </p>

                    {isNoArt && !isVotingPhase && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                            <h4 className="text-sm font-medium text-blue-900 mb-2">Why do I need to upload artwork?</h4>
                            <p className="text-sm text-blue-800">
                                To keep the swap fair, you can only vote on artwork when you have pieces available to trade. This ensures everyone who
                                receives votes has something to offer in return.
                            </p>
                        </div>
                    )}

                    {isAllMatched && !isVotingPhase && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                            <h4 className="text-sm font-medium text-green-900 mb-2">🎉 All your artwork has been matched!</h4>
                            <p className="text-sm text-green-800">
                                Amazing! Every piece you uploaded has found a match. Check your matches to coordinate swaps, or upload more artwork to
                                continue voting.
                            </p>
                        </div>
                    )}

                    {isVotingPhase && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                            <h4 className="text-sm font-medium text-yellow-900 mb-2">{isNoArt ? '⏸️ Next time!' : '🎉 Perfect timing!'}</h4>
                            <p className="text-sm text-yellow-800">
                                {isNoArt
                                    ? 'The voting phase is currently active, so no new artwork can be uploaded. Make sure to upload during the next uploading phase to participate in voting!'
                                    : "You've successfully matched all your artwork during this cycle. When the next uploading phase begins, you can add more pieces to continue participating."}
                            </p>
                        </div>
                    )}

                    <div className="flex justify-center space-x-4">
                        {isNoArt && !isVotingPhase && isPhaseAllowed('upload') && (
                            <button
                                onClick={() => (window.location.href = '/upload')}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Upload Your First Artwork
                            </button>
                        )}

                        {isAllMatched && (
                            <button
                                onClick={() => (window.location.href = '/matches')}
                                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                            >
                                View My Matches
                            </button>
                        )}

                        {isAllMatched && !isVotingPhase && isPhaseAllowed('upload') && (
                            <button
                                onClick={() => (window.location.href = '/upload')}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Upload More Artwork
                            </button>
                        )}

                        {isVotingPhase && (
                            <button
                                onClick={() => (window.location.href = '/dashboard')}
                                className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                            >
                                Back to Dashboard
                            </button>
                        )}
                    </div>

                    {/* Show next phase info during voting phase */}
                    {isVotingPhase && phase.nextTransition && (
                        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h4 className="text-sm font-medium text-blue-900 mb-2">📅 Next Phase</h4>
                            <p className="text-sm text-blue-800">
                                The next <span className="font-medium">{phase.nextTransition.nextPhase}</span> phase
                                {phase.nextTransition.nextPhase === 'Uploading' ? ' (when you can upload artwork) ' : ' '}
                                starts{' '}
                                {new Date(phase.nextTransition.transitionAt).toLocaleDateString('en-US', {
                                    month: 'long',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                })}
                                .
                            </p>
                        </div>
                    )}

                    {/* Show stats if user has some voting history */}
                    {stats.votesGiven.total > 0 && (
                        <div className="bg-white rounded-lg shadow-md p-6 max-w-md mx-auto mt-8">
                            <h4 className="text-lg font-medium text-gray-900 mb-4">Your Voting Stats</h4>
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                    <div className="text-2xl font-bold text-blue-600">{stats.votesGiven.total}</div>
                                    <div className="text-sm text-gray-600">Total Votes</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-green-600">{stats.votesGiven.likes}</div>
                                    <div className="text-sm text-gray-600">Likes</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-purple-600">{stats.matches}</div>
                                    <div className="text-sm text-gray-600">Matches</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="flex items-center justify-center h-96">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading artwork...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!currentArt) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="text-center py-12">
                    <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">All caught up!</h3>
                    <p className="text-gray-600 mb-4">You've voted on all available artwork. Check back later for new pieces!</p>

                    {/* Voting Stats */}
                    <div className="bg-white rounded-lg shadow-md p-6 max-w-md mx-auto">
                        <h4 className="text-lg font-medium text-gray-900 mb-4">Your Voting Stats</h4>
                        <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                                <div className="text-2xl font-bold text-blue-600">{stats.votesGiven.total}</div>
                                <div className="text-sm text-gray-600">Total Votes</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-green-600">{stats.votesGiven.likes}</div>
                                <div className="text-sm text-gray-600">Likes</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-purple-600">{stats.matches}</div>
                                <div className="text-sm text-gray-600">Matches</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const baseUrl = apiUrl;

    return (
        <div className="max-w-4xl mx-auto p-6">
            {/* Header */}
            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Discover Art</h1>
                <p className="text-gray-600">Vote on artwork you'd love to swap with</p>

                {/* Quick Stats */}
                <div className="flex justify-center space-x-6 mt-4 text-sm text-gray-600">
                    <div>
                        <span className="font-medium">{stats.votesGiven.total}</span> votes
                    </div>
                    <div>
                        <span className="font-medium">{stats.matches}</span> matches
                    </div>
                    <div>
                        <span className="font-medium">{stats.votesGiven.likes}</span> likes
                    </div>
                </div>
            </div>

            {/* Main Voting Interface */}
            <div className="flex flex-col items-center">
                {/* Art Card */}
                <div className="voting-card w-full max-w-md mx-auto mb-8">
                    {/* Image Section */}
                    <div className="relative">
                        {currentArt.images && currentArt.images.length > 0 ? (
                            <img
                                src={`${baseUrl}/api/uploads/${currentArt.images[selectedImageIndex]}`}
                                alt={currentArt.title}
                                className="voting-card-image"
                            />
                        ) : (
                            <div className="w-full h-80 bg-gray-200 flex items-center justify-center">
                                <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            </div>
                        )}

                        {/* Image Counter */}
                        {currentArt.images && currentArt.images.length > 1 && (
                            <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                                {selectedImageIndex + 1} / {currentArt.images.length}
                            </div>
                        )}
                    </div>

                    {/* Thumbnail Navigation */}
                    {currentArt.images && currentArt.images.length > 1 && (
                        <div className="flex justify-center space-x-2 p-4 bg-gray-50">
                            {currentArt.images.map((image, index) => (
                                <button
                                    key={index}
                                    onClick={() => setSelectedImageIndex(index)}
                                    className={`w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${
                                        selectedImageIndex === index
                                            ? 'border-blue-500 ring-2 ring-blue-200'
                                            : 'border-gray-300 hover:border-gray-400'
                                    }`}
                                >
                                    <img
                                        src={`${baseUrl}/api/uploads/${image}`}
                                        alt={`${currentArt.title} - ${index + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Art Details */}
                    <div className="p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-2">{currentArt.title}</h2>

                        {/* Artist Info */}
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-700">{getArtistInitials(currentArt.artist)}</span>
                            </div>
                            <div>
                                <button
                                    onClick={() => navigate(`/profile/${currentArt.artist.id}`)}
                                    className="text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline transition-colors"
                                >
                                    {currentArt.artist?.firstName} {currentArt.artist?.lastName}
                                </button>
                            </div>
                        </div>

                        {/* Description */}
                        {currentArt.description && <p className="text-gray-700 mb-4 text-sm leading-relaxed">{currentArt.description}</p>}

                        {/* Metadata */}
                        <div className="space-y-2 text-xs text-gray-500">
                            {currentArt.medium && (
                                <div>
                                    <span className="font-medium">Medium:</span> {currentArt.medium}
                                </div>
                            )}
                            {currentArt.dimensions && (
                                <div>
                                    <span className="font-medium">Dimensions:</span> {currentArt.dimensions}
                                </div>
                            )}
                            {currentArt.yearCreated && (
                                <div>
                                    <span className="font-medium">Year:</span> {currentArt.yearCreated}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Voting Actions */}
                <div className="flex justify-center space-x-8 mb-6">
                    <button
                        onClick={handlePass}
                        disabled={submittingVote}
                        className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-white shadow-lg transition-all duration-200 transform hover:scale-105"
                    >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    <button
                        onClick={handleLike}
                        disabled={submittingVote}
                        className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-white shadow-lg transition-all duration-200 transform hover:scale-105"
                    >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                            />
                        </svg>
                    </button>
                </div>

                {/* Keyboard Shortcuts */}
                <div className="text-center text-sm text-gray-500">
                    <p>
                        Use keyboard shortcuts: <kbd className="px-2 py-1 bg-gray-100 rounded">←</kbd> or{' '}
                        <kbd className="px-2 py-1 bg-gray-100 rounded">X</kbd> to pass, <kbd className="px-2 py-1 bg-gray-100 rounded">→</kbd> or{' '}
                        <kbd className="px-2 py-1 bg-gray-100 rounded">L</kbd> to like
                    </p>
                </div>
            </div>

            {/* Match Modal */}
            {showMatch && matchData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 transform transition-all">
                        <div className="text-center">
                            <div className="text-6xl mb-4">🎉</div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">It's a Match!</h3>
                            <p className="text-gray-600 mb-6">
                                You and {matchData.user1Id === user.id ? matchData.user2?.firstName : matchData.user1?.firstName} both liked each
                                other's artwork!
                            </p>

                            <div className="space-y-4 mb-6">
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <h4 className="font-medium text-gray-900">Your piece:</h4>
                                    <p className="text-gray-600">{matchData.art1?.title}</p>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <h4 className="font-medium text-gray-900">Their piece:</h4>
                                    <p className="text-gray-600">{matchData.art2?.title}</p>
                                </div>
                            </div>

                            <p className="text-sm text-gray-500 mb-4">
                                Both artists have been notified via email. Visit your matches to coordinate the swap!
                            </p>

                            <button
                                onClick={closeMatchModal}
                                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Continue Voting
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export const Matches = () => {
    const {user} = useAuth();
    const navigate = useNavigate();

    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchMatches = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/matches');
            setMatches(response.data.matches);
            setError(null);
        } catch (error) {
            console.error('Error fetching matches:', error);
            setError('Failed to load matches');
        } finally {
            setLoading(false);
        }
    };

    const getStatusDisplay = (status) => {
        const statusConfig = {
            pending: {
                label: 'New Match',
                className: 'bg-blue-100 text-blue-800',
                icon: '🆕',
                description: 'Recently matched',
            },
            confirmed: {
                label: 'Confirmed',
                className: 'bg-green-100 text-green-800',
                icon: '✅',
                description: 'Both artists confirmed',
            },
            completed: {
                label: 'Completed',
                className: 'bg-purple-100 text-purple-800',
                icon: '🎉',
                description: 'Swap completed',
            },
            cancelled: {
                label: 'Cancelled',
                className: 'bg-red-100 text-red-800',
                icon: '❌',
                description: 'Match cancelled',
            },
        };
        return statusConfig[status] || statusConfig.pending;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getArtistInitials = (artist) => {
        if (!artist) return '?';
        return `${artist.firstName?.charAt(0) || ''}${artist.lastName?.charAt(0) || ''}`;
    };

    useEffect(() => {
        fetchMatches();
    }, []);

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading matches...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-7xl mx-auto p-6">
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error</h3>
                            <div className="mt-2 text-sm text-red-700">
                                <p>{error}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const baseUrl = apiUrl;

    return (
        <div className="max-w-7xl mx-auto p-6">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">My Matches</h1>
                <p className="text-gray-600">
                    {matches.length > 0
                        ? `You have ${matches.length} match${matches.length === 1 ? '' : 'es'}! Coordinate with other artists to arrange your swaps.`
                        : 'No matches yet. Keep voting to find artists who love your work!'}
                </p>
            </div>

            {matches.length === 0 ? (
                <div className="text-center py-12">
                    <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                            />
                        </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No matches yet</h3>
                    <p className="text-gray-600 mb-6">Upload artwork and start voting to find artists who want to swap with you!</p>
                    <div className="flex justify-center space-x-4">
                        <button
                            onClick={() => navigate('/voting')}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Start Voting
                        </button>
                        <button
                            onClick={() => navigate('/upload')}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                        >
                            Upload Artwork
                        </button>
                    </div>
                </div>
            ) : (
                <div className="space-y-8">
                    {matches.map((match) => {
                        const status = getStatusDisplay(match.status);
                        const otherUser = match.otherUser;
                        const myArt = match.currentUserArt;
                        const theirArt = match.otherUserArt;

                        return (
                            <div key={match.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                                {/* Match Header */}
                                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3">
                                            <span
                                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.className}`}
                                            >
                                                <span className="mr-1">{status.icon}</span>
                                                {status.label}
                                            </span>
                                            <span className="text-sm text-gray-500">Matched {formatDate(match.matchedAt)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Match Content */}
                                <div className="p-6">
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                        {/* Your Artwork */}
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                                                Your Artwork
                                            </h3>

                                            <div className="bg-gray-50 rounded-lg p-4">
                                                {/* Your Art Image */}
                                                <div className="relative mb-4">
                                                    {myArt.images && myArt.images.length > 0 ? (
                                                        <img
                                                            src={`${baseUrl}/api/uploads/${myArt.images[0]}`}
                                                            alt={myArt.title}
                                                            className="w-full h-48 object-cover rounded-lg"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                                                            <svg
                                                                className="w-12 h-12 text-gray-400"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 48 48"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={2}
                                                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                                />
                                                            </svg>
                                                        </div>
                                                    )}

                                                    {myArt.images && myArt.images.length > 1 && (
                                                        <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                                                            +{myArt.images.length - 1} more
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Your Art Details */}
                                                <div>
                                                    <h4 className="font-semibold text-gray-900 mb-2">{myArt.title}</h4>
                                                    {myArt.description && (
                                                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{myArt.description}</p>
                                                    )}

                                                    <div className="space-y-1 text-xs text-gray-500">
                                                        {myArt.medium && (
                                                            <div>
                                                                <span className="font-medium">Medium:</span> {myArt.medium}
                                                            </div>
                                                        )}
                                                        {myArt.dimensions && (
                                                            <div>
                                                                <span className="font-medium">Dimensions:</span> {myArt.dimensions}
                                                            </div>
                                                        )}
                                                        {myArt.yearCreated && (
                                                            <div>
                                                                <span className="font-medium">Year:</span> {myArt.yearCreated}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Their Artwork */}
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                                Their Artwork
                                            </h3>

                                            <div className="bg-gray-50 rounded-lg p-4">
                                                {/* Their Art Image */}
                                                <div className="relative mb-4">
                                                    {theirArt.images && theirArt.images.length > 0 ? (
                                                        <img
                                                            src={`${baseUrl}/api/uploads/${theirArt.images[0]}`}
                                                            alt={theirArt.title}
                                                            className="w-full h-48 object-cover rounded-lg"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                                                            <svg
                                                                className="w-12 h-12 text-gray-400"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 48 48"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={2}
                                                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                                />
                                                            </svg>
                                                        </div>
                                                    )}

                                                    {theirArt.images && theirArt.images.length > 1 && (
                                                        <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                                                            +{theirArt.images.length - 1} more
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Their Art Details */}
                                                <div>
                                                    <h4 className="font-semibold text-gray-900 mb-2">{theirArt.title}</h4>
                                                    {theirArt.description && (
                                                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{theirArt.description}</p>
                                                    )}

                                                    <div className="space-y-1 text-xs text-gray-500">
                                                        {theirArt.medium && (
                                                            <div>
                                                                <span className="font-medium">Medium:</span> {theirArt.medium}
                                                            </div>
                                                        )}
                                                        {theirArt.dimensions && (
                                                            <div>
                                                                <span className="font-medium">Dimensions:</span> {theirArt.dimensions}
                                                            </div>
                                                        )}
                                                        {theirArt.yearCreated && (
                                                            <div>
                                                                <span className="font-medium">Year:</span> {theirArt.yearCreated}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Artist Information */}
                                    <div className="mt-6 pt-6 border-t border-gray-200">
                                        <h4 className="text-sm font-medium text-gray-900 mb-3">Artist Contact</h4>
                                        <div className="flex items-center space-x-4">
                                            <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                                                <span className="text-sm font-medium text-gray-700">{getArtistInitials(otherUser)}</span>
                                            </div>
                                            <div className="flex-1">
                                                <button
                                                    onClick={() => navigate(`/profile/${otherUser.id}`)}
                                                    className="text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline transition-colors"
                                                >
                                                    {otherUser.firstName} {otherUser.lastName}
                                                </button>

                                                {otherUser.location && <p className="text-sm text-gray-500">{otherUser.location}</p>}
                                            </div>
                                            <div className="text-right">
                                                <a
                                                    href={`mailto:${otherUser.email}?subject=Art Swap - ${myArt.title} for ${theirArt.title}`}
                                                    className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                                >
                                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                    Email
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status Description */}
                                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                                        <p className="text-sm text-blue-800">
                                            <span className="font-medium">{status.description}</span>
                                            {status.label === 'New Match' && ' - Reach out to coordinate your art swap!'}
                                            {status.label === 'Confirmed' && ' - Great! Both artists are ready to swap.'}
                                            {status.label === 'Completed' && ' - This swap has been successfully completed.'}
                                            {status.label === 'Cancelled' && ' - This match was cancelled.'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export const MatchDetail = () => (
    <div className="p-6">
        <h1 className="text-2xl font-bold">Match Details</h1>
        <p>Match detail component coming soon...</p>
    </div>
);

export const Profile = () => {
    const {id} = useParams();
    const {user: currentUser} = useAuth();
    const navigate = useNavigate();

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            // If no ID provided, use current user's profile
            const profileId = id || currentUser.id;
            const response = await axios.get(`/api/auth/profile/${profileId}`);
            setProfile(response.data.user);
            setError(null);
        } catch (error) {
            console.error('Error fetching profile:', error);
            setError(error.response?.status === 404 ? 'User not found' : 'Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
        });
    };

    const getInitials = (firstName, lastName) => {
        return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
    };

    useEffect(() => {
        fetchProfile();
    }, [id, currentUser.id]);

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading profile...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-7xl mx-auto p-6">
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error</h3>
                            <div className="mt-2 text-sm text-red-700">
                                <p>{error}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!profile) {
        return null;
    }

    const baseUrl = apiUrl;

    return (
        <div className="max-w-7xl mx-auto p-6">
            {/* Header */}
            <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-6">
                {/* Personal Info Section */}
                <div className="px-6 py-8">
                    <div className="flex items-start space-x-6">
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                            <div className="w-32 h-32 bg-gray-300 rounded-full flex items-center justify-center">
                                <span className="text-3xl font-medium text-gray-700">{getInitials(profile.firstName, profile.lastName)}</span>
                            </div>
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h1 className="text-3xl font-bold text-gray-900">
                                        {profile.firstName} {profile.lastName}
                                    </h1>
                                    {profile.location && <p className="text-lg text-gray-600 mt-1">📍 {profile.location}</p>}
                                </div>

                                {profile.isOwnProfile && (
                                    <button
                                        onClick={() => navigate('/settings')}
                                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                                    >
                                        Edit Profile
                                    </button>
                                )}
                            </div>

                            {/* Bio */}
                            {profile.bio && (
                                <div className="mt-4">
                                    <p className="text-gray-700 leading-relaxed">{profile.bio}</p>
                                </div>
                            )}

                            {/* Member Since & Stats */}
                            <div className="mt-6 flex items-center space-x-8 text-sm text-gray-500">
                                <div>
                                    <span className="font-medium">Member since:</span> {formatDate(profile.stats.memberSince)}
                                </div>
                                <div>
                                    <span className="font-medium">{profile.stats.totalArtworks}</span> artworks uploaded
                                </div>
                                <div>
                                    <span className="font-medium">{profile.stats.matchedArtworks}</span> successful matches
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Portfolio Section */}
            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">Portfolio</h2>
                    <p className="text-sm text-gray-600 mt-1">{profile.isOwnProfile ? 'Your artwork' : `${profile.firstName}'s artwork`}</p>
                </div>

                <div className="p-6">
                    {profile.artworks && profile.artworks.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {profile.artworks.map((artwork) => (
                                <div key={artwork.id} className="group cursor-pointer" onClick={() => navigate(`/art/${artwork.id}`)}>
                                    <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden group-hover:shadow-lg transition-shadow">
                                        {artwork.images && artwork.images.length > 0 ? (
                                            <img
                                                src={`${baseUrl}/api/uploads/${artwork.images[0]}`}
                                                alt={artwork.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                    />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-3">
                                        <h3 className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                                            {artwork.title}
                                        </h3>
                                        {artwork.medium && <p className="text-xs text-gray-500 mt-1">{artwork.medium}</p>}
                                        {artwork.isMatched && (
                                            <span className="inline-flex items-center px-2 py-1 mt-2 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                                🎉 Matched
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                            </svg>
                            <h3 className="mt-4 text-lg font-medium text-gray-900">
                                {profile.isOwnProfile ? 'No artwork yet' : 'No artwork to display'}
                            </h3>
                            <p className="mt-2 text-gray-500">
                                {profile.isOwnProfile
                                    ? 'Upload your first artwork to get started!'
                                    : `${profile.firstName} hasn't uploaded any artwork yet.`}
                            </p>
                            {profile.isOwnProfile && (
                                <button
                                    onClick={() => navigate('/upload')}
                                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    Upload Artwork
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const MyArt = () => {
    const {user} = useAuth();
    const {phase, isPhaseAllowed} = usePhase();
    const navigate = useNavigate();

    const [artworks, setArtworks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const fetchMyArt = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/art/my-art');
            setArtworks(response.data.art);
            setError(null);
        } catch (error) {
            console.error('Error fetching artwork:', error);
            setError('Failed to load artwork');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (artId) => {
        if (!window.confirm('Are you sure you want to delete this artwork? This action cannot be undone.')) {
            return;
        }

        try {
            setDeletingId(artId);
            await axios.delete(`/api/art/${artId}`);
            toast.success('Artwork deleted successfully');
            fetchMyArt(); // Refresh the list
        } catch (error) {
            console.error('Error deleting artwork:', error);
            const errorMessage = error.response?.data?.error || 'Failed to delete artwork';
            toast.error(errorMessage);
        } finally {
            setDeletingId(null);
        }
    };

    const getArtworkStatus = (artwork) => {
        if (artwork.isMatched) {
            return {
                label: 'Matched',
                className: 'bg-green-100 text-green-800',
                icon: '🎉',
            };
        } else if (!artwork.isAvailable) {
            return {
                label: 'Unavailable',
                className: 'bg-gray-100 text-gray-800',
                icon: '⏸️',
            };
        } else {
            return {
                label: 'Available',
                className: 'bg-blue-100 text-blue-800',
                icon: '✨',
            };
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    useEffect(() => {
        fetchMyArt();
    }, []);

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-7xl mx-auto p-6">
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error</h3>
                            <div className="mt-2 text-sm text-red-700">
                                <p>{error}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto p-6">
            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">My Artwork</h1>
                            <p className="mt-1 text-sm text-gray-600">Manage your uploaded artwork and track their status</p>
                        </div>
                        {isPhaseAllowed('upload') && (
                            <button
                                onClick={() => navigate('/upload')}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                            >
                                Upload New Art
                            </button>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {artworks.length === 0 ? (
                        <div className="text-center py-12">
                            <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                            </svg>
                            <h3 className="mt-2 text-lg font-medium text-gray-900">No artwork uploaded yet</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {isPhaseAllowed('upload')
                                    ? 'Get started by uploading your first piece of artwork.'
                                    : 'Artwork can only be uploaded during the "Uploading" phase.'}
                            </p>
                            {isPhaseAllowed('upload') && (
                                <div className="mt-6">
                                    <button
                                        onClick={() => navigate('/upload')}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                    >
                                        Upload Your First Artwork
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {artworks.map((artwork) => {
                                const status = getArtworkStatus(artwork);
                                const mainImage = artwork.images && artwork.images.length > 0 ? artwork.images[0] : null;

                                return (
                                    <div key={artwork.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                                        {/* Image */}
                                        <div className="relative">
                                            {mainImage ? (
                                                <img
                                                    src={`${apiUrl}/api/uploads/${mainImage}`}
                                                    alt={artwork.title}
                                                    className="w-full h-48 object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                                                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                </div>
                                            )}

                                            {/* Status Badge */}
                                            <div className="absolute top-2 right-2">
                                                <span
                                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.className}`}
                                                >
                                                    <span className="mr-1">{status.icon}</span>
                                                    {status.label}
                                                </span>
                                            </div>

                                            {/* Multiple Images Indicator */}
                                            {artwork.images && artwork.images.length > 1 && (
                                                <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                                                    +{artwork.images.length - 1} more
                                                </div>
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div className="p-4">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">{artwork.title}</h3>
                                            {artwork.description && <p className="text-sm text-gray-600 mb-3 line-clamp-2">{artwork.description}</p>}
                                            <div className="space-y-1 text-xs text-gray-500">
                                                {artwork.medium && (
                                                    <div>
                                                        <span className="font-medium">Medium:</span> {artwork.medium}
                                                    </div>
                                                )}
                                                {artwork.dimensions && (
                                                    <div>
                                                        <span className="font-medium">Dimensions:</span> {artwork.dimensions}
                                                    </div>
                                                )}
                                                {artwork.yearCreated && (
                                                    <div>
                                                        <span className="font-medium">Year:</span> {artwork.yearCreated}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="px-4 pb-4 flex justify-between items-center">
                                            <button
                                                onClick={() => navigate(`/art/${artwork.id}`)}
                                                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                                            >
                                                View Details
                                            </button>

                                            <div className="flex space-x-2">
                                                {isPhaseAllowed('edit-art') && !artwork.isMatched && (
                                                    <button
                                                        onClick={() => navigate(`/art/${artwork.id}/edit`)}
                                                        className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                                                    >
                                                        Edit
                                                    </button>
                                                )}

                                                {isPhaseAllowed('delete-art') && !artwork.isMatched && (
                                                    <button
                                                        onClick={() => handleDelete(artwork.id)}
                                                        disabled={deletingId === artwork.id}
                                                        className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors disabled:opacity-50"
                                                    >
                                                        {deletingId === artwork.id ? 'Deleting...' : 'Delete'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const ArtDetail = () => {
    const {id} = useParams();
    const {user} = useAuth();
    const navigate = useNavigate();

    const [artwork, setArtwork] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    const fetchArtwork = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/art/${id}`);
            setArtwork(response.data.art);
            setError(null);
        } catch (error) {
            console.error('Error fetching artwork:', error);
            setError(error.response?.status === 404 ? 'Artwork not found' : 'Failed to load artwork');
        } finally {
            setLoading(false);
        }
    };

    const getArtworkStatus = (artwork) => {
        if (artwork.isMatched) {
            return {
                label: 'Matched',
                className: 'bg-green-100 text-green-800',
                icon: '🎉',
            };
        } else if (!artwork.isAvailable) {
            return {
                label: 'Unavailable',
                className: 'bg-gray-100 text-gray-800',
                icon: '⏸️',
            };
        } else {
            return {
                label: 'Available',
                className: 'bg-blue-100 text-blue-800',
                icon: '✨',
            };
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const isOwner = artwork && user && artwork.userId === user.id;

    useEffect(() => {
        fetchArtwork();
    }, [id]);

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-7xl mx-auto p-6">
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error</h3>
                            <div className="mt-2 text-sm text-red-700">
                                <p>{error}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!artwork) {
        return null;
    }

    const status = getArtworkStatus(artwork);
    const baseUrl = apiUrl;

    return (
        <div className="max-w-7xl mx-auto p-6">
            {/* Back Button */}
            <div className="mb-6">
                <button onClick={() => navigate(-1)} className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back
                </button>
            </div>

            {/* Artwork Display */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Images Section */}
                <div>
                    {/* Main Image */}
                    <div className="relative mb-4">
                        {artwork.images && artwork.images.length > 0 ? (
                            <img
                                src={`${baseUrl}/api/uploads/${artwork.images[selectedImageIndex]}`}
                                alt={artwork.title}
                                className="w-full h-96 object-cover rounded-lg shadow-lg"
                            />
                        ) : (
                            <div className="w-full h-96 bg-gray-200 flex items-center justify-center rounded-lg">
                                <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            </div>
                        )}

                        {/* Image Counter */}
                        {artwork.images && artwork.images.length > 1 && (
                            <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                                {selectedImageIndex + 1} of {artwork.images.length}
                            </div>
                        )}
                    </div>

                    {/* Thumbnail Navigation */}
                    {artwork.images && artwork.images.length > 1 && (
                        <div className="flex justify-center space-x-2">
                            {artwork.images.map((image, index) => (
                                <button
                                    key={index}
                                    onClick={() => setSelectedImageIndex(index)}
                                    className={`flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-all ${
                                        selectedImageIndex === index
                                            ? 'border-blue-500 ring-2 ring-blue-200'
                                            : 'border-gray-300 hover:border-gray-400'
                                    }`}
                                >
                                    <img
                                        src={`${baseUrl}/api/uploads/${image}`}
                                        alt={`${artwork.title} - ${index + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Details Section */}
                <div className="space-y-6">
                    {/* Title and Status */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h1 className="text-3xl font-bold text-gray-900">{artwork.title}</h1>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${status.className}`}>
                                <span className="mr-1">{status.icon}</span>
                                {status.label}
                            </span>
                        </div>
                    </div>

                    {/* Artist Info */}
                    <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                        <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center">
                            <span className="text-lg font-medium text-gray-700">
                                {artwork.artist?.firstName?.[0]}
                                {artwork.artist?.lastName?.[0]}
                            </span>
                        </div>
                        <div>
                            <button
                                onClick={() => navigate(`/profile/${artwork.artist.id}`)}
                                className="text-lg font-medium text-gray-900 hover:text-blue-600 hover:underline transition-colors"
                            >
                                {artwork.artist?.firstName} {artwork.artist?.lastName}
                            </button>

                            {artwork.artist?.location && <p className="text-sm text-gray-500">{artwork.artist.location}</p>}
                        </div>
                    </div>

                    {/* Description */}
                    {artwork.description && (
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Description</h3>
                            <p className="text-gray-700 leading-relaxed">{artwork.description}</p>
                        </div>
                    )}

                    {/* Metadata */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-3">Details</h3>
                        <div className="space-y-3">
                            {artwork.medium && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Medium:</span>
                                    <span className="text-gray-900">{artwork.medium}</span>
                                </div>
                            )}
                            {artwork.dimensions && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Dimensions:</span>
                                    <span className="text-gray-900">{artwork.dimensions}</span>
                                </div>
                            )}
                            {artwork.yearCreated && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Year Created:</span>
                                    <span className="text-gray-900">{artwork.yearCreated}</span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-gray-600">Uploaded:</span>
                                <span className="text-gray-900">{formatDate(artwork.createdAt)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Owner Actions */}
                    {isOwner && (
                        <div className="pt-4 border-t">
                            <div className="flex space-x-3">
                                <button
                                    onClick={() => navigate(`/art/${artwork.id}/edit`)}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    Edit Artwork
                                </button>
                                <button
                                    onClick={() => navigate('/my-art')}
                                    className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                                >
                                    My Art
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const Settings = () => (
    <div className="p-6">
        <h1 className="text-2xl font-bold">Settings</h1>
        <p>Settings component coming soon...</p>
    </div>
);
